/* eslint-disable enforce-ids-in-jsx/missing-ids */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled, { css, keyframes } from 'styled-components'

import { isBefore, isValid } from 'utils/dateUtils'
import { useMount } from 'utils/hooks'

import { paginateSimulations } from 'containers/SpearPhishingAgent/actions'
import {
  GET_EMAIL_PREVIEW_ASYNC,
  GET_PARTICIPANT_DETAILS_ASYNC,
  GET_SIMULATION_LIST_ASYNC,
  RESET_SIMULATION_PARTICIPANTS,
  SIMULATIONS_PAGE_SIZE,
} from 'containers/SpearPhishingAgent/constants'
import {
  selectSimulationData,
  selectStatusCountsData,
} from 'containers/SpearPhishingAgent/selectors'

import { Table } from 'components/lib/DesignSystem/V3'
import Pagination from 'components/lib/DesignSystem/V3/Pagination'
import Skeleton from 'components/lib/DesignSystem/V3/Skeleton'
import NoResultsState from 'components/pages/SpearPhishingAgent/components/Dashboard/components/CardSection/components/NoResultsState'
import { trackPsimaEmailPreviewOpen } from 'components/pages/SpearPhishingAgent/psimaPendo'

import { NAV_ACTIONS } from '../../navActions'
import { getRowAfterPageCross, sortParticipantsByRisk } from '../../utils'

import EmptyState from './components/EmptyState/EmptyState'
import ListErrorState from './components/ListErrorState/ListErrorState'
import SimulationDrawer, { DRAWER_VIEWS } from './components/SimulationDrawer'
import createParticipantColumns from './participantColumns'
import createSimulationColumns from './simulationColumns'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--t-spacing-4);
`

/* The row set is wider than narrow viewports; let it scroll inside the page rather than
   pushing the whole layout sideways. */
/* Rows replacing the skeleton fade in over 150ms (opacity only); the frame itself stays. */
const rowsArrive = keyframes`
  from {
    opacity: 0;
  }
`

const TableWrapper = styled.div`
  overflow-x: auto;
  background: var(--color-surface);
  border: 1px solid var(--card-border-color);
  border-radius: var(--t-borders-radius-rounded-2xl);
  box-shadow: var(--shadow-1);

  ${({ $arrive }) =>
    $arrive &&
    css`
      animation: ${rowsArrive} 150ms ease-out backwards;
    `}

  tbody td {
    transition: background-color 150ms ease;
  }

  /* The DS marks a clicked row with a hairline only; with the drawer open beside the table
     that is too quiet to tell which simulation is showing (Natali, 3.9), so the open row
     gets a clear primary tint. */
  tbody tr.is-open td {
    background: var(--t-opacity-primary-10);
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    tbody td {
      transition: none;
    }
  }
`

/* Enough rows to fill the viewport without pretending to know the real count. */
const SKELETON_ROW_COUNT = 8

const SimulationsTable = ({
  sort,
  onSortChange,
  selectedSimulationIds = [],
  onToggleSimulation,
  onToggleAll,
}) => {
  const dispatch = useDispatch()
  const simulationData = useSelector(selectSimulationData)
  const statusCountsData = useSelector(selectStatusCountsData)
  const [drawerCampaignId, setDrawerCampaignId] = useState(null)
  const [drawerView, setDrawerView] = useState(DRAWER_VIEWS.DETAILS)
  /* -1 / 1 when the drawer content was reached with the up/down arrows, 0 from a click. */
  const [drawerNavDirection, setDrawerNavDirection] = useState(0)
  const [clickedRowsByCampaign, setClickedRowsByCampaign] = useState({})
  const pendingNavRef = useRef(null)
  const singleParticipantPendingRequestsRef = useRef(new Set())
  const pageNumber = simulationData.page
  const totalPages = simulationData.totalPages || 1
  const totalItems = simulationData.totalItems || 0

  const hasParticipantDetails = useCallback(
    (id) => Object.hasOwn(simulationData.participantDetails, id),
    [simulationData.participantDetails]
  )

  const requestParticipantDetailsIfMissing = useCallback(
    (id) => {
      if (!id) {
        return
      }

      const isParticipantLoading = simulationData.participantsLoaders.includes(id)
      const isPendingRequest = singleParticipantPendingRequestsRef.current.has(id)
      /* A roster that already failed is not re-requested on every render; that looped a
         failing endpoint forever. Only the drawer's Try again asks for it again. */
      const hasFailed = Boolean(simulationData.participantErrors?.[id])

      if (hasParticipantDetails(id) || isParticipantLoading || isPendingRequest || hasFailed) {
        return
      }

      singleParticipantPendingRequestsRef.current.add(id)
      dispatch(GET_PARTICIPANT_DETAILS_ASYNC.initiate({ id }))
    },
    [
      dispatch,
      hasParticipantDetails,
      simulationData.participantErrors,
      simulationData.participantsLoaders,
    ]
  )

  /* A failed roster request is not "missing", so the de-dup above would refuse it. */
  const retryParticipantDetails = useCallback(
    (id) => {
      if (!id) return
      singleParticipantPendingRequestsRef.current.delete(id)
      dispatch(GET_PARTICIPANT_DETAILS_ASYNC.initiate({ id }))
    },
    [dispatch]
  )

  const retryEmailPreview = useCallback(
    (participant) => {
      if (!participant) return
      dispatch(
        GET_EMAIL_PREVIEW_ASYNC.initiate({
          id: participant.campaignId,
          participantId: participant.id,
        })
      )
    },
    [dispatch]
  )

  const handleRowClick = useCallback((campaignId, row) => {
    setClickedRowsByCampaign((prevState) => ({
      ...prevState,
      [campaignId]: row,
    }))
  }, [])

  /** A row or its eye keeps the drawer open and swaps it to the email review. */
  const handlePreviewClick = useCallback(
    (row, event) => {
      event?.stopPropagation()
      handleRowClick(row.campaignId, row)
      setDrawerNavDirection(0)
      setDrawerCampaignId(row.campaignId)
      setDrawerView(DRAWER_VIEWS.PREVIEW)
      trackPsimaEmailPreviewOpen({ simulationId: row.campaignId })
      dispatch(
        GET_EMAIL_PREVIEW_ASYNC.initiate({
          id: row.campaignId,
          participantId: row.id,
        })
      )
    },
    [dispatch, handleRowClick]
  )

  const columns = useMemo(
    () => createParticipantColumns({ onPreviewClick: handlePreviewClick }),
    [handlePreviewClick]
  )

  const participantsByCampaign = useMemo(() => {
    const nextData = {}

    Object.entries(simulationData.participantDetails).forEach(([id, participants]) => {
      nextData[id] = sortParticipantsByRisk(
        participants.map((participant) => ({
          ...participant,
          campaignId: id,
        }))
      )
    })

    return nextData
  }, [simulationData.participantDetails])

  const handleParticipantNavigation = useCallback(
    (action) => {
      if (!drawerCampaignId) {
        return
      }

      const participants = participantsByCampaign[drawerCampaignId] || []
      const clickedRow = clickedRowsByCampaign[drawerCampaignId]

      if (!participants.length || !clickedRow) {
        return
      }

      const currentParticipantIndex = participants.findIndex(({ id }) => id === clickedRow.id)

      if (currentParticipantIndex === -1) {
        return
      }

      const targetParticipantIndex =
        action === NAV_ACTIONS.PREV ? currentParticipantIndex - 1 : currentParticipantIndex + 1

      if (targetParticipantIndex < 0 || targetParticipantIndex >= participants.length) {
        return
      }

      const targetParticipant = participants[targetParticipantIndex]
      setDrawerNavDirection(action === NAV_ACTIONS.PREV ? -1 : 1)
      handleRowClick(drawerCampaignId, targetParticipant)

      dispatch(
        GET_EMAIL_PREVIEW_ASYNC.initiate({
          id: drawerCampaignId,
          participantId: targetParticipant.id,
        })
      )
    },
    [clickedRowsByCampaign, dispatch, drawerCampaignId, handleRowClick, participantsByCampaign]
  )

  /** Opening a row shows its details in the drawer; clicking it again closes it. */
  const toggleDetails = (id) => {
    setDrawerNavDirection(0)
    const isSameRow = drawerCampaignId === id && drawerView === DRAWER_VIEWS.DETAILS

    if (isSameRow) {
      setDrawerCampaignId(null)
      return
    }

    setDrawerCampaignId(id)
    setDrawerView(DRAWER_VIEWS.DETAILS)
    requestParticipantDetailsIfMissing(id)
  }

  const isValidUntilExpired = (validUntil) => {
    const validUntilDate = new Date(validUntil)

    if (!isValid(validUntilDate)) {
      return false
    }

    return isBefore(validUntilDate, new Date())
  }

  useMount(() => {
    dispatch({
      type: GET_SIMULATION_LIST_ASYNC.INITIATE,
    })

    return () => {
      singleParticipantPendingRequestsRef.current.clear()
      dispatch({
        type: GET_SIMULATION_LIST_ASYNC.CANCEL,
      })
      dispatch({
        type: RESET_SIMULATION_PARTICIPANTS,
      })
    }
  })

  useEffect(() => {
    singleParticipantPendingRequestsRef.current.forEach((id) => {
      const isParticipantLoading = simulationData.participantsLoaders.includes(id)

      if (hasParticipantDetails(id) || !isParticipantLoading) {
        singleParticipantPendingRequestsRef.current.delete(id)
      }
    })
  }, [hasParticipantDetails, simulationData.participantsLoaders])

  const visibleSimulations = simulationData.items

  const drawerSimulation = useMemo(
    () => simulationData.items.find(({ id }) => id === drawerCampaignId) || null,
    [drawerCampaignId, simulationData.items]
  )

  /**
   * Step between simulations from inside the drawer, following the order the list is
   * currently showing, so the drawer never disagrees with what is behind it.
   */
  const drawerSimulationIndex = useMemo(
    () => visibleSimulations.findIndex(({ id }) => id === drawerCampaignId),
    [drawerCampaignId, visibleSimulations]
  )

  const openSimulationAt = useCallback(
    (targetId, direction) => {
      setDrawerNavDirection(direction)
      setDrawerCampaignId(targetId)
      setDrawerView(DRAWER_VIEWS.DETAILS)
      requestParticipantDetailsIfMissing(targetId)
    },
    [requestParticipantDetailsIfMissing]
  )

  const handleSimulationNavigation = useCallback(
    (action) => {
      if (pendingNavRef.current || drawerSimulationIndex === -1) return

      const step = action === NAV_ACTIONS.PREV ? -1 : 1

      const neighbourIndex = drawerSimulationIndex + step
      if (neighbourIndex >= 0 && neighbourIndex < visibleSimulations.length) {
        openSimulationAt(visibleSimulations[neighbourIndex].id, step)
        return
      }

      const targetPage = pageNumber + step
      if (targetPage < 1 || targetPage > totalPages) return

      pendingNavRef.current = { action, targetPage }
      setDrawerNavDirection(step)
      setDrawerView(DRAWER_VIEWS.DETAILS)
      dispatch(paginateSimulations(targetPage))
    },
    [dispatch, drawerSimulationIndex, openSimulationAt, pageNumber, totalPages, visibleSimulations]
  )

  const { failedNavigationPage } = simulationData
  const retryListNavigation = useCallback(() => {
    if (!failedNavigationPage) return
    pendingNavRef.current = {
      action: failedNavigationPage > pageNumber ? NAV_ACTIONS.NEXT : NAV_ACTIONS.PREV,
      targetPage: failedNavigationPage,
    }
    dispatch(paginateSimulations(failedNavigationPage))
  }, [dispatch, failedNavigationPage, pageNumber])

  useEffect(() => {
    const pending = pendingNavRef.current
    if (!pending || simulationData.loader) return
    pendingNavRef.current = null
    if (simulationData.navigationError) return

    const target =
      simulationData.page === pending.targetPage
        ? getRowAfterPageCross(pending.action, visibleSimulations)
        : null

    if (!target) {
      setDrawerCampaignId(null)
      return
    }

    openSimulationAt(target.id, pending.action === NAV_ACTIONS.PREV ? -1 : 1)
  }, [
    openSimulationAt,
    simulationData.loader,
    simulationData.navigationError,
    simulationData.page,
    visibleSimulations,
  ])

  /* Nothing launched at all is a different story from nothing matching the filters:
     the first invites you to the dashboard, the second just needs the filter cleared.
     The catalog total is unfiltered; the list page is whatever the query returned. */
  const hasAnySimulations = (statusCountsData?.data?.simulations?.total ?? 0) > 0
  /* The store starts with an empty list and loader off, and the request only goes out in
     useMount, so the first paint would otherwise flash the empty state for one frame before
     the skeleton. Until the first page has actually come back, the list is still loading. */
  /* A page jump must keep the current rows on screen until SUCCESS replaces them
     (fail-closed). Status-chip refetch and the first load still use the skeleton. */
  const isPageJumpPending = Boolean(simulationData.pageJump || simulationData.pendingPage)
  const isListLoading = !simulationData.listFetched || (simulationData.loader && !isPageJumpPending)

  /* The header checkbox covers the rows on screen; "select all matching" in the selection
     bar is what reaches beyond this page. */
  const visibleIds = useMemo(() => visibleSimulations.map(({ id }) => id), [visibleSimulations])

  const simulationColumns = useMemo(
    () =>
      createSimulationColumns({
        selectedIds: selectedSimulationIds,
        visibleIds,
        onToggleSimulation,
        onToggleAll,
      }),
    [onToggleAll, onToggleSimulation, selectedSimulationIds, visibleIds]
  )

  /* Loading is skeleton rows in the real column shape, not a spinner (P5.2). Derived from
     the live columns so the header row, widths and order cannot drift out of sync, which
     also means the table does not resize when the data lands. The select header is dropped
     so there is no live checkbox over rows that do not exist yet. */
  const skeletonColumns = useMemo(
    () =>
      simulationColumns.map(({ key, header, width }) => ({
        key,
        width,
        header: key === 'select' ? '' : header,
        render: () => <Skeleton variant="text" width={key === 'select' ? '16px' : '70%'} />,
      })),
    [simulationColumns]
  )

  const skeletonRows = useMemo(
    () => Array.from({ length: SKELETON_ROW_COUNT }, (_, index) => ({ id: `skeleton-${index}` })),
    []
  )

  let simulationListContent = hasAnySimulations ? <NoResultsState /> : <EmptyState />

  if (simulationData.listError) {
    simulationListContent = (
      <ListErrorState onRetry={() => dispatch({ type: GET_SIMULATION_LIST_ASYNC.INITIATE })} />
    )
  } else if (isListLoading) {
    simulationListContent = (
      <TableWrapper data-testid="Psima_Launched_TableSkeleton_div">
        <Table density="comfortable" columns={skeletonColumns} data={skeletonRows} />
      </TableWrapper>
    )
  } else if (visibleSimulations.length > 0) {
    simulationListContent = (
      <TableWrapper data-testid="Psima_Launched_Table_div" $arrive>
        <Table
          /* The DS default density pads cells 6px vertically, which lands rows at 39px,
             under every step of the DS 3.0 table scale. Comfortable pads 14px. */
          density="comfortable"
          columns={simulationColumns}
          data={visibleSimulations}
          onRowClick={({ id }) => toggleDetails(id)}
          clickedRow={drawerSimulation}
          rowClassName={({ id }) => (id === drawerCampaignId ? 'is-open' : undefined)}
          sortState={sort}
          onSort={onSortChange}
        />
      </TableWrapper>
    )
  }

  return (
    <Container>
      {simulationListContent}
      {!isListLoading && !simulationData.listError && totalItems > 0 && totalPages > 1 && (
        <Pagination
          id="Psima_Launched_Pagination"
          pageCount={totalPages}
          forcePage={Math.max(0, pageNumber - 1)}
          itemsPerPage={SIMULATIONS_PAGE_SIZE}
          totalItems={totalItems}
          resultsLabel="simulations"
          onPageChange={({ selected }) => {
            const targetPage = selected + 1
            if (targetPage === pageNumber && !simulationData.listError) return
            pendingNavRef.current = null
            setDrawerCampaignId(null)
            dispatch(paginateSimulations(targetPage))
          }}
        />
      )}
      <SimulationDrawer
        open={Boolean(drawerCampaignId)}
        view={drawerView}
        onClose={() => {
          pendingNavRef.current = null
          setDrawerCampaignId(null)
        }}
        simulation={drawerSimulation}
        participants={participantsByCampaign[drawerCampaignId] || []}
        participantsLoaded={Boolean(simulationData.participantDetails[drawerCampaignId])}
        participantsError={Boolean(simulationData.participantErrors?.[drawerCampaignId])}
        onRetryParticipants={retryParticipantDetails}
        listNavigationError={Boolean(simulationData.navigationError)}
        onRetryListNavigation={retryListNavigation}
        previewError={Boolean(simulationData.emailPreviewError)}
        onRetryPreview={retryEmailPreview}
        columns={columns}
        clickedRow={clickedRowsByCampaign[drawerCampaignId]}
        activeParticipant={drawerCampaignId ? clickedRowsByCampaign[drawerCampaignId] : null}
        onNavigateParticipant={handleParticipantNavigation}
        onNavigateSimulation={handleSimulationNavigation}
        hasPrevSimulation={
          !isPageJumpPending &&
          drawerSimulationIndex !== -1 &&
          (drawerSimulationIndex > 0 || pageNumber > 1)
        }
        hasNextSimulation={
          !isPageJumpPending &&
          drawerSimulationIndex !== -1 &&
          (drawerSimulationIndex < visibleSimulations.length - 1 || pageNumber < totalPages)
        }
        onBackToDetails={() => {
          setClickedRowsByCampaign(({ [drawerCampaignId]: _previewedRow, ...rest }) => rest)
          setDrawerNavDirection(0)
          setDrawerView(DRAWER_VIEWS.DETAILS)
        }}
        navDirection={drawerNavDirection}
        onPreviewRow={handlePreviewClick}
        isValidUntilExpired={isValidUntilExpired}
      />
    </Container>
  )
}

export default SimulationsTable
